import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { P } from '@fabric-app/ui/components/Typography';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssTextEllipsis, hasHover } from '@fabric-app/ui/constants/styled-utils';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useResourceDataContextSafe } from '../../../../resource-detail/components/context/resourceDataContext.js';
import { useQueryResourceAncestors } from '../../../../resource-detail/queries/useQueryResourceAncestors.js';
import { ResourcePath, NodePathName } from '../../ResourcePath/ResourcePath.js';
import { containerPreviewCompact } from './cssPreviewContainer.js';
import { cssPreviewShadow } from './sharedCss.js';
import { styled } from 'styled-components';
import { useQueryUserInfo } from '../../../../user/queries/useQueryUserInfo.js';
import AvatarUser from '../../../../user/components/AvatarUser/AvatarUser.js';
import { appRoutes } from '../../../../app/routing/routes.config.js';
import { generatePath } from '@fabric-app/utils/url/routing';
import { Link } from '@fabric-app/compat';
import { TITLE_SECTION_HEIGHT } from '@fabric-app/ui/theme/resourcePreviewConstants';
import { isResourceStateProcessing } from '../../../utils/isResourceStateProcessing.js';
import { isResourceDetailBookmark } from '../../../utils/resourceDetailGuards.js';
import Skeleton from '@fabric-app/ui/components/Skeleton';

const PreviewRoot = ({ showRootNameIfShowingAvatar }) => {
  const { data: user } = useQueryUserInfo();
  const { resource } = useResourceDataContextSafe();
  const { accessibleAncestors, refetch, isFetched } = useQueryResourceAncestors(resource.id, {
    enabled: false
  });
  if (!resource.root) return;
  const fetchAncestors = () => {
    if (!isFetched) {
      refetch();
    }
  };
  const hideAvatar = !resource.root.folder.user || resource.root.folder.user.id === user?.id;
  const showRootName = showRootNameIfShowingAvatar && !hideAvatar || hideAvatar;
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx(Tooltip, {
        placement: "top",
        label: /* @__PURE__ */ jsx(StyledResourcePath, {
          path: accessibleAncestors,
          startElement: hideAvatar ? void 0 : /* @__PURE__ */ jsx(AvatarUser, {
            user: resource.root.folder.user,
            size: 16
          })
        }),
        disabled: !isFetched || accessibleAncestors.length < 1,
        delay: 0,
        children: /* @__PURE__ */ jsxs(Flex, {
          alignItems: "center",
          gap: "text",
          overflow: "hidden",
          onMouseOver: fetchAncestors,
          style: !hideAvatar ? {
            marginLeft: -3,
            minWidth: 16
          } : {},
          children: [
            !hideAvatar && /* @__PURE__ */ jsx(AvatarUser, {
              disableTooltip: true,
              user: resource.root.folder.user,
              size: 16
            }),
            showRootName && !hideAvatar && /* @__PURE__ */ jsx("span", {
              children: "/"
            }),
            showRootName && /* @__PURE__ */ jsx(Name, {
              as: Link,
              onClick: (e) => e.stopPropagation(),
              href: generatePath(appRoutes.space.detail, {
                spaceId: resource.root.folder.id
              }),
              children: resource.root.folder.name || "Untitled"
            })
          ]
        })
      }),
      /* @__PURE__ */ jsx("span", {
        children: "/"
      })
    ]
  });
};
const ResourcePreviewName = ({ showRoot }) => {
  const { resource, placeholderName, fullName } = useResourceDataContextSafe();
  const resourceNameAndExtensionLen = (resource.name || "").length + (resource.extension || "").length;
  const isProcessing = isResourceStateProcessing(resource.stateProcessing);
  const isProcesingName = isResourceDetailBookmark(resource) && (isProcessing || resource.isDraft) && (resource.name === null || resource.name === "");
  return /* @__PURE__ */ jsxs(PillName, {
    children: [
      showRoot && !!resource.root && /* @__PURE__ */ jsx(PreviewRoot, {
        showRootNameIfShowingAvatar: resourceNameAndExtensionLen < 10
      }),
      isProcesingName ? /* @__PURE__ */ jsx(Skeleton, {
        height: 13,
        width: "100px",
        borderRadius: 6
      }) : resource.name ? /* @__PURE__ */ jsx(Tooltip, {
        placement: "top",
        label: /* @__PURE__ */ jsx(P, {
          color: "inherit",
          size: "xxs",
          children: fullName
        }),
        delay: 0,
        children: /* @__PURE__ */ jsxs(Flex, {
          alignItems: "center",
          overflow: "hidden",
          children: [
            /* @__PURE__ */ jsx(Name, {
              children: resource.name || /* @__PURE__ */ jsx(Placeholder, {
                children: placeholderName
              })
            }),
            resource.extension && /* @__PURE__ */ jsxs(Ext, {
              children: [
                ".",
                resource.extension
              ]
            })
          ]
        })
      }) : /* @__PURE__ */ jsxs(Flex, {
        alignItems: "center",
        overflow: "hidden",
        children: [
          /* @__PURE__ */ jsx(Placeholder, {
            children: placeholderName
          }),
          resource.extension && /* @__PURE__ */ jsxs(Ext, {
            children: [
              ".",
              resource.extension
            ]
          })
        ]
      })
    ]
  });
};
const Ext = styled.div.withConfig({
  displayName: "ResourcePreviewName__Ext",
  componentId: "sc-64fc461e-0"
})([
  ``
]);
const Name = styled.div.withConfig({
  displayName: "ResourcePreviewName__Name",
  componentId: "sc-64fc461e-1"
})([
  ``,
  `;&:hover{color:`,
  `;}`
], cssTextEllipsis, cssVar["color-app-primary-text"]);
const StyledResourcePath = styled(ResourcePath).withConfig({
  displayName: "ResourcePreviewName__StyledResourcePath",
  componentId: "sc-64fc461e-2"
})([
  ``,
  `{color:white;font-size:0.75rem;}svg{width:1.25rem;height:1.25rem;}`
], NodePathName);
const PillName = styled.div.withConfig({
  displayName: "ResourcePreviewName__PillName",
  componentId: "sc-64fc461e-3"
})([
  `font-size:0.75rem;color:`,
  `;font-weight:500;padding:0 0.75rem;width:max-content;max-width:100%;display:flex;align-items:center;height:`,
  `px;border:1px dashed `,
  `;border-radius:calc(`,
  `px / 2);gap:0.25rem;flex-shrink:0;transition:0.1s all;background-color:`,
  `;`,
  `{&:hover{background:`,
  `;color:`,
  `;`,
  `;}}`,
  `{display:none;}`
], cssVar["color-text-quaternary"], TITLE_SECTION_HEIGHT, cssVar["color-border-primary"], TITLE_SECTION_HEIGHT, cssVar["color-bg-secondary"], hasHover, cssVar["color-bg-primary"], cssVar["color-text-secondary"], cssPreviewShadow, containerPreviewCompact);
const Placeholder = styled(Name).withConfig({
  displayName: "ResourcePreviewName__Placeholder",
  componentId: "sc-64fc461e-4"
})([
  `opacity:0.6;pointer-events:none;`
]);

export { PillName, ResourcePreviewName };
//# sourceMappingURL=ResourcePreviewName.js.map
